// L'adresse qu'un OPÉRATEUR voit pour un logement — sa déclaration dans `address_mapping_table`, sinon le DMP —
// et les doublons de cette adresse entre logements (N37, D0309-6, N38 ; décisions du user des 11-09, 07-10 et 08-10).
//
// Pourquoi ici : la même règle sert au serveur (dire en quoi la déclaration s'écarte du DMP), au tableau des
// logements (afficher « DMP → déclaré », marquer les doublons) et aux deux formulaires qui écrivent la table de
// mapping (prévenir PENDANT la saisie). Trois copies auraient divergé à la première retouche.
//
// La comparaison (décision du 08-10) : sans casse ni espaces, et une extension qui ne fait que RÉPÉTER le numéro
// vaut une extension vide — le DMP écrit « 27 / 27 » là où un opérateur déclare « 27 / (rien) », c'est la même
// adresse. Rien d'autre n'est rapproché : accents, ponctuation et zéros de tête comptent, et une rue déclarée
// dans l'autre langue EST un écart.
// Pur et sans dépendance.
/** La forme sous laquelle deux valeurs déclarées se comparent : sans casse ni espaces. */
export function declaredKey(value) {
    return (value ?? '').toLowerCase().replace(/\s+/g, '');
}
/** L'extension telle qu'elle COMPTE : vide quand elle ne fait que répéter le numéro. */
export function extensionKey(houseNumber, extension) {
    const ext = declaredKey(extension);
    return ext === declaredKey(houseNumber) ? '' : ext;
}
/**
 * En quoi la déclaration s'écarte du DMP, champ par champ (rue, numéro, extension). La boîte garde son propre
 * champ, `mappedBusNumber`, posé ailleurs et inchangé.
 * Rien n'est rendu quand le DMP n'a pas de numéro (liste servie depuis l'instantané du ticket : on ne compare pas
 * à ce qu'on ne connaît pas), ni pour un champ que la déclaration laisse vide (rue, numéro : ce n'en est pas une).
 */
export function declaredOverlay(dmp, declared) {
    const out = {};
    if (!declared || declaredKey(dmp.houseNumber) === '')
        return out;
    const street = (declared.streetName ?? '').trim();
    if (street !== '' && declaredKey(street) !== declaredKey(dmp.streetName))
        out.mappedStreetName = street;
    const number = (declared.houseNumber ?? '').trim();
    if (number !== '' && declaredKey(number) !== declaredKey(dmp.houseNumber))
        out.mappedHouseNumber = number;
    // L'extension se juge face au numéro de SON côté : « 29 / 29 » déclaré sur « 27 / 27 » ne change que le numéro.
    const declaredNumber = number !== '' ? number : dmp.houseNumber;
    if (extensionKey(declaredNumber, declared.houseNumberExtension) !== extensionKey(dmp.houseNumber, dmp.houseNumberExtension)) {
        out.mappedHouseNumberExtension = (declared.houseNumberExtension ?? '').trim();
    }
    return out;
}
/**
 * L'adresse que VOIT L'OPÉRATEUR pour ce logement : la déclaration là où elle s'écarte, sinon le DMP. L'extension
 * est rendue telle qu'elle compte (vide si elle répète le numéro vu), pour que « 27 / 27 » redéclaré « 29 » ne
 * devienne pas « 29 / 27 ».
 */
export function seenAddressOf(u) {
    const houseNumber = (u.mappedHouseNumber ?? u.houseNumber ?? '').trim();
    const rawExtension = u.mappedHouseNumberExtension != null
        ? u.mappedHouseNumberExtension
        // Pas d'écart d'extension : celle du DMP vaut face au numéro du DMP — vide si elle le répétait.
        : (extensionKey(u.houseNumber, u.houseNumberExtension) === '' ? '' : (u.houseNumberExtension ?? ''));
    const extension = rawExtension.trim();
    return {
        streetName: (u.mappedStreetName ?? u.streetName ?? '').trim(),
        houseNumber,
        houseNumberExtension: extensionKey(houseNumber, extension) === '' ? '' : extension,
        busNumber: (u.mappedBusNumber ?? u.busNumber ?? '').trim(),
    };
}
/** La clé d'une adresse complète (rue + numéro + extension + boîte) — `null` sans numéro : rien à comparer. */
export function addressKey(a) {
    const number = declaredKey(a.houseNumber);
    if (number === '')
        return null;
    return [declaredKey(a.streetName), number, extensionKey(a.houseNumber, a.houseNumberExtension), declaredKey(a.busNumber)].join('|');
}
const comparable = (u) => (u.added ? null : addressKey(seenAddressOf(u)));
/**
 * Les logements de la liste qui partagent leur adresse vue avec un autre : jvid → les AUTRES jvids (triés).
 * Un logement sans doublon n'est pas dans la table. La liste est celle de la fenêtre : rien n'est cherché ailleurs.
 */
export function findDeclaredDuplicates(units) {
    const byKey = new Map();
    for (const u of units) {
        const key = comparable(u);
        if (key == null)
            continue;
        const list = byKey.get(key);
        if (list) {
            if (!list.includes(u.jvid))
                list.push(u.jvid);
        }
        else
            byKey.set(key, [u.jvid]);
    }
    const out = new Map();
    for (const jvids of byKey.values()) {
        if (jvids.length < 2)
            continue;
        for (const jvid of jvids)
            out.set(jvid, jvids.filter((j) => j !== jvid).sort((a, b) => a - b));
    }
    return out;
}
/**
 * Pour un formulaire : les AUTRES logements de la liste dont l'adresse vue est celle que l'on est en train de
 * saisir pour `jvid` (triés). Vide tant que la saisie n'a pas de numéro.
 */
export function duplicatesOfInput(units, jvid, input) {
    const key = addressKey(input);
    if (key == null)
        return [];
    const out = [];
    for (const u of units) {
        if (u.jvid === jvid || out.includes(u.jvid))
            continue;
        if (comparable(u) === key)
            out.push(u.jvid);
    }
    return out.sort((a, b) => a - b);
}
//# sourceMappingURL=declared-address.js.map